import React, { useState } from "react";
import api, { formatApiError } from "@/lib/upr-api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll get back to you soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="contact-page">
      <div className="upr-eyebrow mb-6"><span className="upr-divider"/>Get in touch</div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-8xl" style={{ letterSpacing: "-0.035em" }}>
        Contact.
      </h1>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16 mt-12">
        <div className="md:col-span-5">
          <div className="upr-eyebrow mb-2">Label & demos</div>
          <a className="upr-link-underline font-heading text-lg" href="mailto:hello@universopositivorecords.com" data-testid="email-link">
            hello@universopositivorecords.com
          </a>
          <div className="mt-8 upr-eyebrow mb-2">Press</div>
          <a className="upr-link-underline font-heading text-lg" href="mailto:press@universopositivorecords.com">
            press@universopositivorecords.com
          </a>
          <div className="mt-8 upr-eyebrow mb-2">Off-platform</div>
          <a className="upr-link-underline font-heading text-lg" href="https://universopositivo.bandcamp.com/" target="_blank" rel="noreferrer">
            Bandcamp →
          </a>
          <div className="mt-12 p-6" style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }}>
            <div className="upr-eyebrow mb-3">Demos policy</div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--upr-muted)" }}>
              We listen to everything but only reply to projects that we want to release.
              Send a private Bandcamp / SoundCloud link, no attachments, plus a short note.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="md:col-span-7 space-y-5" data-testid="contact-form">
          <Field id="name" label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
          <Field id="email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email" />
          <Field id="subject" label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
          <div>
            <label htmlFor="message" className="upr-eyebrow block mb-2">Message</label>
            <textarea
              id="message"
              required
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="contact-message"
              className="w-full p-3 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] transition-colors"
              style={{ borderColor: "var(--upr-border)" }}
            />
          </div>
          <button type="submit" disabled={sending} className="upr-btn" data-testid="contact-submit">
            {sending ? "Sending…" : sent ? "Sent — send another" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label htmlFor={id} className="upr-eyebrow block mb-2">{label}</label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full p-3 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] transition-colors"
        style={{ borderColor: "var(--upr-border)" }}
      />
    </div>
  );
}
